import { Check, Plus, X } from "@icons";
import classNames from "classnames";
import { ChevronDown } from "lucide-react";
import {
  ReactNode,
  Ref,
  createContext,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { FieldProps } from "../_shared/field";
import FieldLabel from "../_shared/field-label";
import FieldMessage from "../_shared/field-message";

export interface ComboboxOption {
  value: string;
  /** Text shown for the option, in the menu and (single-select) in the field. */
  label: string;
  /** Render the option greyed out and unselectable. */
  disabled?: boolean;
  /** Small badge shown on a disabled option (e.g. "Locked"). */
  hint?: string;
}

export interface ComboboxGroup {
  /** Section heading; omit for an ungrouped list. */
  group?: string;
  items: ComboboxOption[];
}

/** Overridable UI strings (English defaults; pass translated copy from the app). */
export interface ComboboxLabels {
  selectAll: string;
  deselectAll: string;
  clear: string;
  /** Menu toolbar count, e.g. `(n) => `${n} selected``. */
  selectedCount: (count: number) => string;
  /** Collapsed-chips pill, e.g. `(n) => `+${n} more``. */
  more: (count: number) => string;
  showLess: string;
  empty: string;
  searchPlaceholder: string;
  /** Create-row prefix; the typed query is appended in bold. */
  create: string;
  /** Fallback badge on a disabled option that has no `hint`. */
  unavailable: string;
  /** Shown in the field and menu while `loading`. */
  loading: string;
}

const DEFAULT_LABELS: ComboboxLabels = {
  selectAll: "Select all",
  deselectAll: "Deselect all",
  clear: "Clear",
  selectedCount: (count) => `${count} selected`,
  more: (count) => `+${count} more`,
  showLess: "Show less",
  empty: "No results",
  searchPlaceholder: "Search…",
  create: "Create",
  unavailable: "Unavailable",
  loading: "Loading…",
};

/**
 * App-wide overrides for the built-in UI strings of every {@link Combobox} and
 * {@link Select}. Wrap the app in a provider supplying translated copy so the
 * controls localize without a `labels` prop at each call site; the per-instance
 * `labels` prop still wins over the context.
 */
export const ControlLabelsContext = createContext<Partial<ComboboxLabels>>({});

function Spinner({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={classNames(
        "inline-block w-[15px] h-[15px] shrink-0 rounded-full border-2 border-gray-200 border-t-primary-400 animate-spin",
        className,
      )}
    />
  );
}

const CREATED_OPTION_GROUP = "Custom";
const CREATED_VALUE_PREFIX = "custom:";

/** Extra scroll so the active option isn't flush against the menu edge. */
const ACTIVE_OPTION_SCROLL_MARGIN = 4;

/** Popup sizing/placement (px). The popup is portaled and positioned against the field. */
const MENU_MAX_HEIGHT = 288;
const MENU_GAP = 6;
const MENU_VIEWPORT_MARGIN = 8;

const CHIP_OVERFLOW_BUTTON_CLASS =
  "inline-flex items-center h-[26px] px-2.5 rounded-full border border-dashed border-gray-300 bg-transparent text-xs font-medium text-gray-500 cursor-pointer transition-colors hover:bg-[#f5f7fb] hover:text-gray-700 hover:border-gray-400";

/**
 * Suppress the default mousedown on menu buttons and options so the click
 * doesn't blur the search input and collapse the menu before it's handled.
 */
function keepSearchInputFocused(event: React.MouseEvent) {
  event.preventDefault();
}

/**
 * The nearest ancestor that establishes a containing block for `position: fixed`
 * (a transform/filter/perspective/will-change/contain), e.g. a centered Radix
 * dialog. The portaled menu is hosted here — and its fixed coordinates measured
 * relative to it — so it positions correctly and stays inside that element's
 * focus scope. Returns null when fixed resolves against the viewport.
 */
function findFixedContainingBlock(
  element: HTMLElement | null,
): HTMLElement | null {
  let node = element?.parentElement ?? null;
  while (node && node !== document.body) {
    const style = getComputedStyle(node);
    if (
      style.transform !== "none" ||
      style.perspective !== "none" ||
      style.filter !== "none" ||
      /transform|perspective|filter/.test(style.willChange) ||
      /paint|layout|strict|content/.test(style.contain)
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

function indexOptionsByValue(
  options: ComboboxOption[],
): Record<string, ComboboxOption> {
  const optionByValue: Record<string, ComboboxOption> = {};
  options.forEach((option) => (optionByValue[option.value] = option));
  return optionByValue;
}

function ComboboxToolbar({
  allSelected,
  selectedCount,
  strings,
  onSelectAll,
  onClear,
}: {
  allSelected: boolean;
  selectedCount: number;
  strings: ComboboxLabels;
  onSelectAll: () => void;
  onClear: () => void;
}) {
  const buttonClass =
    "px-1.5 py-0.5 rounded border-0 bg-transparent text-xs font-medium text-primary-600 cursor-pointer transition-colors hover:bg-primary-50";

  return (
    <div className="flex shrink-0 items-center justify-between px-2 py-1.5 border-b border-gray-100">
      <button
        type="button"
        className={buttonClass}
        onMouseDown={keepSearchInputFocused}
        onClick={allSelected ? onClear : onSelectAll}
      >
        {allSelected ? strings.deselectAll : strings.selectAll}
      </button>
      <span className="text-xs text-gray-400">
        {strings.selectedCount(selectedCount)}
      </span>
      <button
        type="button"
        className={classNames(
          buttonClass,
          "disabled:text-gray-400 disabled:hover:bg-transparent disabled:cursor-default",
        )}
        onMouseDown={keepSearchInputFocused}
        onClick={onClear}
        disabled={selectedCount === 0}
      >
        {strings.clear}
      </button>
    </div>
  );
}

function OptionMarker({
  multiple,
  selected,
}: {
  multiple: boolean;
  selected: boolean;
}) {
  if (multiple) {
    return (
      <span
        className={classNames(
          "grid place-items-center w-[17px] h-[17px] shrink-0 rounded border-[1.5px] text-white transition-[background,border-color]",
          selected ? "bg-primary-500 border-primary-500" : "border-gray-300",
        )}
      >
        {selected && <Check size={12} strokeWidth={2.5} />}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center justify-center w-4 h-4 shrink-0 text-primary-500">
      {selected && <Check size={15} strokeWidth={2.25} />}
    </span>
  );
}

function ComboboxOptionRow({
  id,
  option,
  multiple,
  selected,
  active,
  navIndex,
  unavailableLabel,
  onActivate,
  onSelect,
}: {
  id?: string;
  option: ComboboxOption;
  multiple: boolean;
  selected: boolean;
  active: boolean;
  navIndex?: number;
  unavailableLabel: string;
  onActivate: () => void;
  onSelect: () => void;
}) {
  return (
    <div
      id={id}
      data-nav={option.disabled ? undefined : navIndex}
      role="option"
      aria-selected={selected}
      aria-disabled={option.disabled || undefined}
      onMouseEnter={() => {
        if (!option.disabled) onActivate();
      }}
      onMouseDown={keepSearchInputFocused}
      onClick={onSelect}
      className={classNames(
        "flex items-center gap-2.5 p-2 rounded leading-tight text-gray-700",
        option.disabled ? "opacity-45 cursor-not-allowed" : "cursor-pointer",
        selected && "font-medium text-gray-900",
        active && "bg-[#f5f7fb]",
      )}
    >
      <OptionMarker multiple={multiple} selected={selected} />
      <span className="flex-1 min-w-0">{option.label}</span>
      {option.disabled && (
        <span className="shrink-0 rounded-full border border-gray-200 px-2 py-px text-[11px] text-gray-500">
          {option.hint ?? unavailableLabel}
        </span>
      )}
    </div>
  );
}

function ComboboxCreateRow({
  id,
  query,
  active,
  standalone,
  navIndex,
  prefix,
  onActivate,
  onCreate,
}: {
  id: string;
  query: string;
  active: boolean;
  standalone: boolean;
  navIndex: number;
  prefix: string;
  onActivate: () => void;
  onCreate: () => void;
}) {
  return (
    <div
      id={id}
      data-nav={navIndex}
      role="option"
      aria-selected={false}
      onMouseEnter={onActivate}
      onMouseDown={keepSearchInputFocused}
      onClick={onCreate}
      className={classNames(
        "flex items-center gap-2.5 p-2 rounded cursor-pointer font-medium text-primary-600",
        standalone && "mt-0.5",
        active && "bg-primary-50",
      )}
    >
      <Plus size={16} strokeWidth={1.75} />
      <span className="flex-1 min-w-0">
        {prefix} <span className="text-gray-900">“{query}”</span>
      </span>
    </div>
  );
}

function ComboboxChips({
  options,
  maxChips,
  expanded,
  disabled,
  strings,
  onRemove,
  onExpand,
  onCollapse,
}: {
  options: ComboboxOption[];
  maxChips: number;
  expanded: boolean;
  disabled: boolean;
  strings: ComboboxLabels;
  onRemove: (value: string) => void;
  onExpand: () => void;
  onCollapse: () => void;
}) {
  const visibleOptions = expanded ? options : options.slice(0, maxChips);
  const hiddenCount = options.length - visibleOptions.length;

  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-3">
      {visibleOptions.map((option) => (
        <span
          key={option.value}
          className="inline-flex items-center gap-1.5 h-[26px] pl-2.5 pr-1 rounded-full bg-primary-50 text-primary-700 text-[13px] font-medium max-w-full"
        >
          <span className="overflow-hidden text-ellipsis whitespace-nowrap">
            {option.label}
          </span>
          {!disabled && (
            <button
              type="button"
              onClick={() => onRemove(option.value)}
              aria-label={`Remove ${option.label}`}
              title={`Remove ${option.label}`}
              className="inline-flex items-center justify-center w-[18px] h-[18px] shrink-0 p-0 border-0 bg-transparent rounded-full text-primary-400 cursor-pointer transition-colors hover:bg-primary-100 hover:text-primary-700"
            >
              <X size={13} />
            </button>
          )}
        </span>
      ))}
      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={onExpand}
          className={CHIP_OVERFLOW_BUTTON_CLASS}
        >
          {strings.more(hiddenCount)}
        </button>
      )}
      {expanded && options.length > maxChips && (
        <button
          type="button"
          onClick={onCollapse}
          className={CHIP_OVERFLOW_BUTTON_CLASS}
        >
          {strings.showLess}
        </button>
      )}
    </div>
  );
}

export interface ComboboxProps
  extends Omit<FieldProps, "valid" | "defaultBadge"> {
  /** Flat option list; a convenience alternative to {@link groups}. */
  options?: ComboboxOption[];
  /** Grouped option list (takes precedence over {@link options}). */
  groups?: ComboboxGroup[];
  /** Selected values (controlled). Single-select carries at most one. */
  value: string[];
  /** Fires with the next full selection. */
  onChange: (value: string[]) => void;
  /** Multi-select: per-option checkboxes, a select-all toolbar and a chips panel. */
  multiple?: boolean;
  placeholder?: string;
  /** Type-to-filter (default). When false the field is a plain click-to-open dropdown. */
  searchable?: boolean;
  /** Show a spinner in the field and menu, e.g. while options are being fetched. */
  loading?: boolean;
  /** Show the clear (×) affordance when there is a selection (default true). */
  clearable?: boolean;
  /**
   * Server-driven search: called with the query on each keystroke. Supplying it
   * switches off client-side filtering (the parent returns already-filtered options).
   */
  onSearchChange?: (query: string) => void;
  /** Offer a "Create …" row when the query matches no option. */
  allowCreate?: boolean;
  /** Called with the typed label when a create row is chosen. */
  onCreate?: (label: string) => void;
  /** Chips past this count collapse into a "+N more" toggle. */
  maxChips?: number;
  /** Valid state passthrough for the shared message row. */
  valid?: string | boolean;
  /** Always-on muted helper text below the field. */
  hint?: string;
  /** Actions rendered at the right end of the label row (e.g. a "manage" link). */
  labelActions?: ReactNode;
  /** Overrides for the built-in UI strings. */
  labels?: Partial<ComboboxLabels>;
  /** Notified when the control loses focus (react-hook-form integration). */
  onBlur?: () => void;
  /** Ref to the search input — used by {@link ControlledCombobox} to wire react-hook-form's field ref. */
  inputRef?: Ref<HTMLInputElement>;
}

export function Combobox({
  options,
  groups,
  value,
  onChange,
  multiple = false,
  label,
  required = false,
  info,
  infoOpen = false,
  placeholder,
  searchable = true,
  loading = false,
  clearable = true,
  onSearchChange,
  allowCreate = false,
  onCreate,
  maxChips = 6,
  error,
  valid,
  hint,
  disabled = false,
  name,
  labelActions,
  labels,
  onBlur,
  inputRef,
}: ComboboxProps) {
  const contextLabels = useContext(ControlLabelsContext);
  const strings = { ...DEFAULT_LABELS, ...contextLabels, ...labels };

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [chipsExpanded, setChipsExpanded] = useState(false);
  const [createdOptions, setCreatedOptions] = useState<ComboboxOption[]>([]);
  const [menuPosition, setMenuPosition] = useState<{
    left: number;
    width: number;
    maxHeight: number;
    top?: number;
    bottom?: number;
  } | null>(null);
  const [portalHost, setPortalHost] = useState<HTMLElement | null>(null);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const fieldRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const reactId = useId();
  const inputId = name ?? reactId;
  const listboxId = `${inputId}-listbox`;

  const selectedValues = useMemo(() => new Set(value), [value]);
  const hasError = !!error;

  const availableGroups = useMemo<ComboboxGroup[]>(() => {
    const base = groups ?? (options ? [{ items: options }] : []);
    if (createdOptions.length === 0) return base;
    return [...base, { group: CREATED_OPTION_GROUP, items: createdOptions }];
  }, [groups, options, createdOptions]);

  const optionByValue = useMemo(
    () => indexOptionsByValue(availableGroups.flatMap((group) => group.items)),
    [availableGroups],
  );

  const normalizedQuery = query.trim().toLowerCase();
  const clientSideFilter = searchable && !onSearchChange;
  const matchingGroups = useMemo(() => {
    if (!clientSideFilter || !normalizedQuery) return availableGroups;
    return availableGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((option) =>
          option.label.toLowerCase().includes(normalizedQuery),
        ),
      }))
      .filter((group) => group.items.length > 0);
  }, [availableGroups, normalizedQuery, clientSideFilter]);

  const navigableOptions = useMemo(
    () =>
      matchingGroups.flatMap((group) =>
        group.items.filter((option) => !option.disabled),
      ),
    [matchingGroups],
  );
  const navIndexByValue = useMemo(() => {
    const byValue: Record<string, number> = {};
    navigableOptions.forEach(
      (option, index) => (byValue[option.value] = index),
    );
    return byValue;
  }, [navigableOptions]);

  const queryMatchesExistingOption = Object.values(optionByValue).some(
    (option) => option.label.toLowerCase() === normalizedQuery,
  );
  const showCreateRow =
    allowCreate && normalizedQuery.length > 0 && !queryMatchesExistingOption;
  const createRowIndex = navigableOptions.length;
  const navigableCount = navigableOptions.length + (showCreateRow ? 1 : 0);

  const selectedOptions = useMemo(
    () =>
      value.map(
        (selectedValue) =>
          optionByValue[selectedValue] ?? {
            value: selectedValue,
            label: selectedValue,
          },
      ),
    [value, optionByValue],
  );
  const singleSelectedOption = multiple ? undefined : selectedOptions[0];

  const selectableOptions = useMemo(
    () =>
      availableGroups.flatMap((group) =>
        group.items.filter((option) => !option.disabled),
      ),
    [availableGroups],
  );
  const allOptionsSelected =
    selectableOptions.length > 0 &&
    selectableOptions.every((option) => selectedValues.has(option.value));

  function assignRef(node: HTMLInputElement | null) {
    searchRef.current = node;
    if (typeof inputRef === "function") inputRef(node);
    else if (inputRef)
      (inputRef as React.MutableRefObject<HTMLInputElement | null>).current =
        node;
  }

  useEffect(() => {
    setActiveIndex(0);
  }, [normalizedQuery, open]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const list = listRef.current;
    const activeOption = list.querySelector<HTMLElement>(
      `[data-nav="${activeIndex}"]`,
    );
    if (!activeOption) return;
    const optionTop = activeOption.offsetTop;
    const optionBottom = optionTop + activeOption.offsetHeight;
    if (optionTop < list.scrollTop) {
      list.scrollTop = optionTop - ACTIVE_OPTION_SCROLL_MARGIN;
    } else if (optionBottom > list.scrollTop + list.clientHeight) {
      list.scrollTop =
        optionBottom - list.clientHeight + ACTIVE_OPTION_SCROLL_MARGIN;
    }
  }, [activeIndex, open]);

  // Position the portaled popup against the field, flipping upward when there
  // isn't room below, and keep it attached while any ancestor scrolls. The menu
  // is hosted in the nearest fixed-containing-block ancestor (a transformed
  // modal, etc.) or <body>, with coordinates measured relative to that host.
  useLayoutEffect(() => {
    if (!open) return;
    const containingBlock = findFixedContainingBlock(fieldRef.current);
    setPortalHost(containingBlock ?? document.body);

    function positionMenu() {
      const field = fieldRef.current;
      if (!field) return;
      const rect = field.getBoundingClientRect();
      const host = containingBlock?.getBoundingClientRect();
      const originLeft = host ? host.left : 0;
      const originTop = host ? host.top : 0;
      const hostHeight = host ? host.height : window.innerHeight;

      const fieldTop = rect.top - originTop;
      const fieldBottom = rect.bottom - originTop;
      const spaceBelow = hostHeight - fieldBottom;
      const spaceAbove = fieldTop;
      const openUpward =
        spaceBelow < MENU_MAX_HEIGHT + MENU_GAP && spaceAbove > spaceBelow;
      const available =
        (openUpward ? spaceAbove : spaceBelow) -
        MENU_GAP -
        MENU_VIEWPORT_MARGIN;

      setMenuPosition({
        left: rect.left - originLeft,
        width: rect.width,
        maxHeight: Math.max(0, Math.min(MENU_MAX_HEIGHT, available)),
        ...(openUpward
          ? { bottom: hostHeight - fieldTop + MENU_GAP }
          : { top: fieldBottom + MENU_GAP }),
      });
    }

    positionMenu();
    window.addEventListener("scroll", positionMenu, true);
    window.addEventListener("resize", positionMenu);
    return () => {
      window.removeEventListener("scroll", positionMenu, true);
      window.removeEventListener("resize", positionMenu);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function closeOnOutsidePointer(event: MouseEvent) {
      const target = event.target as Node;
      if (
        rootRef.current?.contains(target) ||
        listRef.current?.contains(target)
      )
        return;
      setOpen(false);
      setQuery("");
    }
    document.addEventListener("mousedown", closeOnOutsidePointer);
    return () =>
      document.removeEventListener("mousedown", closeOnOutsidePointer);
  }, [open]);

  function commit(nextSelection: Set<string>) {
    onChange([...nextSelection]);
  }

  function openMenu() {
    if (disabled) return;
    setOpen(true);
    searchRef.current?.focus();
  }

  function toggleOption(optionValue: string) {
    if (optionByValue[optionValue]?.disabled) return;
    if (multiple) {
      const nextSelection = new Set(selectedValues);
      if (nextSelection.has(optionValue)) nextSelection.delete(optionValue);
      else nextSelection.add(optionValue);
      commit(nextSelection);
      searchRef.current?.focus();
    } else {
      commit(new Set([optionValue]));
      setOpen(false);
      setQuery("");
    }
  }

  function removeSelection(optionValue: string) {
    const nextSelection = new Set(selectedValues);
    nextSelection.delete(optionValue);
    commit(nextSelection);
  }

  function clearSelection(event?: React.MouseEvent) {
    event?.stopPropagation();
    commit(new Set());
    setQuery("");
    setChipsExpanded(false);
  }

  function selectAllOptions() {
    const nextSelection = new Set(selectedValues);
    selectableOptions.forEach((option) => nextSelection.add(option.value));
    commit(nextSelection);
    searchRef.current?.focus();
  }

  function createOptionFromQuery() {
    const createdLabel = query.trim();
    if (!createdLabel) return;
    const createdValue = `${CREATED_VALUE_PREFIX}${createdLabel.toLowerCase()}`;
    if (!optionByValue[createdValue]) {
      setCreatedOptions((previous) => [
        ...previous,
        { value: createdValue, label: createdLabel },
      ]);
    }
    onCreate?.(createdLabel);
    if (multiple) {
      commit(new Set(selectedValues).add(createdValue));
      setQuery("");
      searchRef.current?.focus();
    } else {
      commit(new Set([createdValue]));
      setOpen(false);
      setQuery("");
    }
  }

  function chooseActiveOption() {
    if (activeIndex < navigableOptions.length) {
      toggleOption(navigableOptions[activeIndex].value);
    } else if (showCreateRow) {
      createOptionFromQuery();
    }
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!open) return openMenu();
      setActiveIndex((index) => Math.min(index + 1, navigableCount - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (!open) return openMenu();
      chooseActiveOption();
    } else if (event.key === "Escape") {
      setOpen(false);
      setQuery("");
    } else if (
      event.key === "Backspace" &&
      !query &&
      multiple &&
      selectedValues.size > 0
    ) {
      removeSelection([...selectedValues][selectedValues.size - 1]);
    }
  }

  const resolvedPlaceholder =
    placeholder ?? (multiple ? "Select options" : "Select an option");
  const searchingNow = searchable && open;

  function inputPlaceholderText(): string {
    if (multiple)
      return searchingNow ? strings.searchPlaceholder : resolvedPlaceholder;
    return singleSelectedOption?.label ?? resolvedPlaceholder;
  }

  const inputValue = searchingNow ? query : (singleSelectedOption?.label ?? "");

  const optionDomId = (navIndex: number) => `${listboxId}-option-${navIndex}`;
  const createRowDomId = `${listboxId}-create`;
  const activeDescendantId =
    !open || loading
      ? undefined
      : showCreateRow && activeIndex === createRowIndex
        ? createRowDomId
        : navigableOptions[activeIndex]
          ? optionDomId(activeIndex)
          : undefined;
  const listboxLabel = typeof label === "string" ? label : undefined;

  const menu = (
    <div
      style={{
        position: "fixed",
        left: menuPosition?.left,
        width: menuPosition?.width,
        maxHeight: menuPosition?.maxHeight,
        top: menuPosition?.top,
        bottom: menuPosition?.bottom,
      }}
      className="z-[1000] flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg font-sans text-sm"
    >
      {multiple && !loading && (
        <ComboboxToolbar
          allSelected={allOptionsSelected}
          selectedCount={selectedValues.size}
          strings={strings}
          onSelectAll={selectAllOptions}
          onClear={() => clearSelection()}
        />
      )}

      <div
        ref={listRef}
        id={listboxId}
        role="listbox"
        aria-multiselectable={multiple || undefined}
        aria-label={listboxLabel}
        aria-busy={loading || undefined}
        className="relative min-h-0 flex-1 overflow-y-auto p-1"
      >
        {loading && (
          <div className="flex items-center justify-center gap-2 py-[18px] text-[13px] text-gray-400">
            <Spinner />
            {strings.loading}
          </div>
        )}

        {!loading && matchingGroups.length === 0 && !showCreateRow && (
          <div className="px-2 pt-5 pb-2 text-center text-sm text-gray-400">
            {strings.empty}
          </div>
        )}

        {!loading &&
          matchingGroups.map((group, groupIndex) => (
            <div
              key={group.group ?? `group-${groupIndex}`}
              role={group.group ? "group" : undefined}
              aria-label={group.group}
              className="[&+&]:mt-0.5"
            >
              {group.group && (
                <div className="px-2 pt-2 pb-0.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                  {group.group}
                </div>
              )}
              {group.items.map((option) => {
                const navIndex = navIndexByValue[option.value];
                return (
                  <ComboboxOptionRow
                    key={option.value}
                    id={option.disabled ? undefined : optionDomId(navIndex)}
                    option={option}
                    multiple={multiple}
                    selected={selectedValues.has(option.value)}
                    active={!option.disabled && navIndex === activeIndex}
                    navIndex={navIndex}
                    unavailableLabel={strings.unavailable}
                    onActivate={() => setActiveIndex(navIndex)}
                    onSelect={() => toggleOption(option.value)}
                  />
                );
              })}
            </div>
          ))}

        {!loading && showCreateRow && (
          <ComboboxCreateRow
            id={createRowDomId}
            query={query.trim()}
            active={createRowIndex === activeIndex}
            standalone={matchingGroups.length === 0}
            navIndex={createRowIndex}
            prefix={strings.create}
            onActivate={() => setActiveIndex(createRowIndex)}
            onCreate={createOptionFromQuery}
          />
        )}
      </div>
    </div>
  );

  return (
    <div ref={rootRef} className="w-full font-sans text-sm">
      <FieldLabel
        htmlFor={inputId}
        label={label}
        required={required}
        info={info}
        infoOpen={infoOpen}
        labelActions={labelActions}
      />

      <div
        ref={fieldRef}
        onClick={openMenu}
        className={classNames(
          "flex items-center min-h-10 pl-3 pr-1.5 rounded border transition-[border-color,box-shadow,background]",
          {
            "bg-white": !disabled,
            "bg-gray-100 cursor-not-allowed": disabled,
            "cursor-text": !disabled && searchable,
            "cursor-pointer": !disabled && !searchable,
            "border-gray-200": !open && !hasError,
            "border-primary-300 ring-2 ring-primary-300/40": open && !hasError,
            "border-red-500 ring-2 ring-red-500/20": hasError,
          },
        )}
      >
        <input
          ref={assignRef}
          id={inputId}
          name={name}
          className={classNames(
            "flex-1 min-w-0 h-[38px] border-0 outline-none bg-transparent text-sm text-gray-800 text-ellipsis disabled:cursor-not-allowed",
            searchable ? "cursor-[inherit]" : "cursor-pointer",
            searchingNow && singleSelectedOption
              ? "placeholder:text-gray-800"
              : "placeholder:text-gray-400",
          )}
          value={inputValue}
          placeholder={inputPlaceholderText()}
          disabled={disabled}
          readOnly={!searchable}
          onChange={(event) => {
            if (!searchable) return;
            setQuery(event.target.value);
            setOpen(true);
            onSearchChange?.(event.target.value);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => !disabled && setOpen(true)}
          onBlur={(event) => {
            const nextFocused = event.relatedTarget as Node | null;
            if (
              nextFocused &&
              (rootRef.current?.contains(nextFocused) ||
                listRef.current?.contains(nextFocused))
            ) {
              return;
            }
            setOpen(false);
            setQuery("");
            onBlur?.();
          }}
          role="combobox"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-activedescendant={activeDescendantId}
          aria-autocomplete={searchable ? "list" : "none"}
          aria-invalid={hasError}
        />
        <div className="flex items-center gap-0.5 shrink-0">
          {loading && <Spinner className="mx-[5px]" />}
          {!loading && clearable && selectedValues.size > 0 && !disabled && (
            <button
              type="button"
              className="inline-flex items-center justify-center w-6 h-6 p-0 border-0 bg-transparent rounded text-gray-400 cursor-pointer transition-colors hover:bg-gray-100 hover:text-gray-700"
              onMouseDown={keepSearchInputFocused}
              onClick={clearSelection}
              aria-label="Clear all selections"
              title={strings.clear}
            >
              <X size={15} />
            </button>
          )}
          <span className="w-px h-4 bg-gray-200 mx-0.5" />
          <span
            className={classNames(
              "inline-flex items-center justify-center w-6 h-6 text-gray-400 transition-transform",
              disabled ? "pointer-events-none" : "cursor-pointer",
              open && "rotate-180",
            )}
            onMouseDown={keepSearchInputFocused}
            onClick={(event) => {
              event.stopPropagation();
              if (disabled) return;
              setOpen((wasOpen) => !wasOpen);
              searchRef.current?.focus();
            }}
            aria-hidden="true"
          >
            <ChevronDown size={16} strokeWidth={1.75} />
          </span>
        </div>
      </div>

      {open &&
        !disabled &&
        menuPosition &&
        portalHost &&
        createPortal(menu, portalHost)}

      {multiple && selectedOptions.length > 0 && (
        <ComboboxChips
          options={selectedOptions}
          maxChips={maxChips}
          expanded={chipsExpanded}
          disabled={disabled}
          strings={strings}
          onRemove={removeSelection}
          onExpand={() => setChipsExpanded(true)}
          onCollapse={() => setChipsExpanded(false)}
        />
      )}

      <FieldMessage
        error={error}
        valid={valid}
        hint={hint}
        isEmpty={selectedOptions.length === 0}
      />
    </div>
  );
}

export default Combobox;
